import i18n, { type i18n as I18nextInstance } from 'i18next';
import { initReactI18next, Trans } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { i18nConfig } from './config';

// 定义t的断言
type TFunction = (key: string, options?: Record<string, any>) => string;
type I18nInstance = I18nextInstance & {
    t: TFunction;
};

// 定义命名空间类型
type Namespace = 'common';

// 定义变量类型
type TranslationVariables = Record<string, string | number | boolean>;

// 定义翻译选项类型 - fallback 现在是必传的
interface TranslationOptions {
    key: string;
    fallback: string;
    namespace?: Namespace;
    variables?: TranslationVariables;
}

// 导出Trans组件
export { Trans };

// 插件 i18n 实例
let pluginI18nInstance: I18nInstance | null = null;
let isInitialized = false;
let initPromise: Promise<void> | null = null;

// 插件初始化函数
export async function initI18n(lng?: string): Promise<void> {
    if (isInitialized) {
        return;
    }

    if (initPromise) {
        return initPromise;
    }

    initPromise = new Promise(async (resolve, reject) => {
        try {
            console.log(`🌐 Sub-luca-plugin initializing i18n ${lng}...`);

            const instance = i18n.createInstance();

            // 异步初始化实例
            await instance
                .use(LanguageDetector)
                .use(initReactI18next)
                .init({
                    ...i18nConfig,
                    lng,
                });

            pluginI18nInstance = instance;
            isInitialized = true;

            console.log(`✅ Sub-luca-plugin i18n ${lng} initialized successfully`);
            resolve();
        } catch (error) {
            console.error(`❌ Sub-luca-plugin failed to initialize i18n ${lng}:`, error);
            reject(error);
        }
    });

    return initPromise;
}

// 获取插件实例
export function getI18nInstance(): I18nInstance {
    if (!pluginI18nInstance || !isInitialized) {
        throw new Error('i18n not initialized. Call initI18n() first.');
    }
    return pluginI18nInstance;
}

// 检查是否已初始化
export function isI18nInitialized(): boolean {
    return isInitialized && pluginI18nInstance !== null;
}

// 核心翻译函数 - fallback 现在是必传的
function translateCore(key: string, fallback: string, namespace?: Namespace, variables?: TranslationVariables): string {
    if (!isInitialized || !pluginI18nInstance) {
        console.warn(`i18n not initialized, using fallback for key: ${key}`);
        return fallback;
    }

    try {
        const translationKey = namespace ? `${namespace}:${key}` : key;
        const options = variables ? variables : {};
        const result = pluginI18nInstance.t(translationKey as any, options);
        return typeof result === 'string' ? result : fallback;
    } catch (error) {
        console.warn(`Translation failed for key: ${key}`, error);
        return fallback;
    }
}

// 原有的函数签名 - fallback 现在是必传的
export function t(key: string, fallback: string, namespace?: Namespace, variables?: TranslationVariables): string;
// 对象参数重载
export function t(options: TranslationOptions): string;
// 实现
export function t(
    keyOrOptions: string | TranslationOptions,
    fallback?: string,
    namespace?: Namespace,
    variables?: TranslationVariables
): string {
    if (typeof keyOrOptions === 'object') {
        // 对象参数调用
        const { key, fallback: fb, namespace: ns, variables: vars } = keyOrOptions;
        return translateCore(key, fb, ns, vars);
    } else {
        // 字符串参数调用
        const key = keyOrOptions;
        if (fallback === undefined) {
            throw new Error('fallback is required when using string parameters');
        }
        return translateCore(key, fallback, namespace, variables);
    }
}

// 创建命名空间翻译器（支持多种调用方式）
export function createNamespaceTranslator(namespace: Namespace) {
    return {
        // 原有方法 - fallback 现在是必传的
        t: (key: string, fallback: string, variables?: TranslationVariables) => translateCore(key, fallback, namespace, variables),

        // 对象参数方法
        translate: (options: Omit<TranslationOptions, 'namespace'>) =>
            translateCore(options.key, options.fallback, namespace, options.variables),
    };
}

// 获取当前语言
export function getCurrentLanguage(): string {
    if (!isInitialized || !pluginI18nInstance) {
        return 'zh';
    }
    return pluginI18nInstance.language || 'zh';
}

// 切换语言
export async function changeLanguage(lng: string): Promise<void> {
    if (!isInitialized || !pluginI18nInstance) {
        console.error('Failed to change language:');
        return;
    }

    try {
        await pluginI18nInstance.changeLanguage(lng);
    } catch (error) {
        console.error('Failed to change language:', error);
    }
}

// 导出常用的命名空间翻译器
export const tCommon = createNamespaceTranslator('common');

// 创建 Hook 用于组件级国际化
export const useTranslation = (namespace: Namespace) => {
    const translator = createNamespaceTranslator(namespace);
    const currentLang = getCurrentLanguage();

    return {
        t: translator.t,
        translate: translator.translate,
        currentLang,
        changeLanguage: async (lang: string) => {
            await changeLanguage(lang);
            // 触发组件重新渲染
        },
    };
};
